import { GridSortItem } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import ToggleSwitch from "../../../../../components/ToggleSwitch";
import ActionMenu from "../../../../../components/app/ActionMenu";
import DrugCatalogDrawer from "../../../../../components/app/admin/Library/catalog/DrugCatalogDrawer";
import {
  ADD_DRUG_CODE_CATALOG_INITIAL_VALUES,
  DrugCodeCatalogFormType,
} from "../../../../../constants/initial-schemas";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useArchiveCatalogCode } from "../../../../../queries/catalog/use-archive-catalog-code";
import useGetCataLogCode from "../../../../../queries/catalog/use-get-catalog-code";
import { useStatusUpdateCatalogCode } from "../../../../../queries/catalog/use-status-update-catalog-code";
import {
  DEFAULT_PAGE_SIZE,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  getSuccessSnackbar,
  isAdmin,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import PageLayout from "../../../../../components/PageLayout";
import { CatalogOutletContext, DrugCatalogRow } from "./catalog-types";
import { useCatalogCodeAccess } from "./hooks/use-catalog-code-access";

const CatalogDrugCatalog = () => {
  const { search: searchKey, isArchieved: archived } =
    useOutletContext<CatalogOutletContext>();
  const hasMasterData = useCatalogCodeAccess("DRUG_CATALOG");

  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const {
    data,
    refetch,
    isLoading: isTableLoading,
    isFetching,
  } = useGetCataLogCode({
    gridSorting,
    pagination,
    searchKey,
    type: "DRUG_CATALOG",
    active: undefined,
    archive: archived,
  });

  const drugEditModalDisclosure = useDisclosure(false);

  const { mutateAsync: updateStatus } = useStatusUpdateCatalogCode();
  const { mutateAsync: deleteDrugCode } = useArchiveCatalogCode();

  const [editDrugCodeValues, setEditDrugCodeValues] =
    useState<DrugCodeCatalogFormType>();
  const [uuid, setUuid] = useState<string>();
  const showSnackbar = useSnackbar();

  const GiveList = (archiveFlag: boolean) => {
    return useMemo(() => {
      return archiveFlag
        ? [{ value: "unarchive", label: "Unarchive" }]
        : [
            { value: "edit", label: "Edit" },
            { value: "delete", label: "Archive" },
          ];
    }, [archiveFlag]); // Recomputes only when 'archiveFlag' changes
  };

  const COLUMNS = applyDefaultColumnSettings<DrugCatalogRow>([
    {
      field: "code",
      headerName: "Drug Code",
      flex: 0.3,
      minWidth: 110,
      sortable: false,
    },
    {
      field: "formType",
      headerName: "Form Type",
      flex: 0.4,
      minWidth: 200,
      sortable: false,
      renderCell: ({ value }) =>
        value ? replaceUnderScoreAddSpace(value.toLowerCase()) : "",
    },
    {
      field: "drugName",
      headerName: "Drug Name",
      flex: 0.6,
      minWidth: 80,
      sortable: false,
    },
    {
      field: "strength",
      headerName: "Strength",
      flex: 0.5,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "active",
      headerName: "Status",
      width: 150,
      sortable: false,
      renderCell: ({ row, value }) => (
        <ToggleSwitch
          id={row.uuid?.toString()}
          checked={value}
          onConfirm={(status, id) =>
            updateStatus({ medicalCodeId: id as string, status }).then(
              (res) => {
                refetch();
                return res;
              }
            )
          }
        />
      ),
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      renderCell: ({ row }) => {
        return (
          <ActionMenu
            options={GiveList(!!row?.archive)}
            onActionClick={(v) => {
              if (v === "edit") {
                setUuid(row.uuid?.toString());
                const transformedValues: DrugCodeCatalogFormType = {
                  ...ADD_DRUG_CODE_CATALOG_INITIAL_VALUES,
                  drugId: row?.code,
                  formType: row?.formType,
                  drugName: row?.drugName,
                  strength: row?.strength,
                  description: row?.description,
                };
                setEditDrugCodeValues(transformedValues);
                drugEditModalDisclosure.open();
              } else {
                deleteDrugCode({
                  medicalCodeId: row?.uuid?.toString() as string,
                  archive: v === "delete",
                }).then((res) => {
                  refetch();
                  showSnackbar(getSuccessSnackbar(res));
                  return res;
                });
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <PageLayout>
      <DrugCatalogDrawer
        uuid={uuid}
        initialValues={editDrugCodeValues}
        dialogTitle="Edit Drug Catalog"
        disclosureHost={drugEditModalDisclosure}
      />

      <CustomDataGridV2
        loading={isTableLoading || isFetching}
        searchString={searchKey}
        rows={data?.data?.content ?? []}
        getRowId={(row) => row?.uuid}
        columns={filterActionCol(COLUMNS, !isAdmin() ? !hasMasterData : false)}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        rowCount={data?.data?.page?.totalElements ?? 10}
      />
    </PageLayout>
  );
};

export default CatalogDrugCatalog;
